import React, { useState, useEffect, useReducer, useRef } from "react";

import { isSameDay, parseISO, format } from "date-fns";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { green } from "@material-ui/core/colors";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import {
  AccessTime,
  Block,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
  PictureAsPdf,
  InsertDriveFile,
} from "@material-ui/icons";

// saveMediaFile (backend) grava "<nome>.<rnd5>.<ext>" quando havia nome
// original; aqui removemos o segmento aleatorio de 5 chars para exibir o nome
// como o cliente enviou (ex.: Receipt-2841.5PqbS.pdf -> Receipt-2841.pdf).
const getOriginalFilename = (mediaUrl) => {
  if (!mediaUrl) return "";
  const name = String(mediaUrl).split("/").pop();
  return name.replace(/\.[A-Za-z0-9]{5}(\.[^.]+)$/, "$1");
};

// Esconde a "legenda" quando ela e apenas o nome do arquivo (sem texto real do
// cliente), para nao repetir o nome embaixo da midia.
const getDisplayBody = (message) => {
  if (!message.mediaUrl) return message.body;
  const original = getOriginalFilename(message.mediaUrl);
  const raw = String(message.mediaUrl).split("/").pop();
  if (message.body === message.mediaUrl || message.body === raw || message.body === original) {
    return "";
  }
  return message.body;
};

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../assets/wa-background.png";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import Audio from "../Audio";

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
  },

  messagesList: {
    backgroundImage: `url(${whatsBackground})`,
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 20px 20px",
    overflowY: "scroll",
    [theme.breakpoints.down("sm")]: {
      paddingBottom: "90px",
    },
    ...theme.scrollbarStyles,
  },

  circleLoading: {
    color: green[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: "#ffffff",
    color: "#303030",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  quotedContainerLeft: {
    margin: "0 0 5px 0",
    overflow: "hidden",
    backgroundColor: "#f5f6f6",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
    minWidth: 180,
  },

  quotedMsg: {
    padding: "6px 12px 8px 8px",
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
    color: "#667781",
    fontSize: "13px",
    lineHeight: "19px",
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
  },

  quotedAuthorMe: {
    display: "block",
    fontWeight: 500,
    fontSize: "12.8px",
    lineHeight: "18px",
    color: "#06cf9c",
  },

  quotedAuthorOther: {
    display: "block",
    fontWeight: 500,
    fontSize: "12.8px",
    lineHeight: "18px",
    color: "#53bdeb",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#53bdeb",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: "#dcf8c6",
    color: "#303030",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    paddingLeft: 5,
    paddingRight: 5,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "0 1px 1px #b3b3b3",
  },

  quotedContainerRight: {
    margin: "0 0 5px 0",
    overflowY: "hidden",
    backgroundColor: "#d1f4cc",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
    minWidth: 180,
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#06cf9c",
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: "#999",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: "#999",
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: "#e1f3fb",
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px #b3b3b3",
  },

  dailyTimestampText: {
    color: "#808888",
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },

  documentCard: {
    display: "flex",
    flexDirection: "column",
    width: 250,
    maxWidth: "100%",
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },

  documentPreview: {
    width: "100%",
    height: 160,
    backgroundColor: "#fff",
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
    overflow: "hidden",
    "& embed, & iframe": {
      width: "100%",
      height: 340,
      marginTop: -8,
      border: "none",
      pointerEvents: "none",
    },
  },

  documentRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    textDecoration: "none",
    color: "inherit",
  },

  documentIcon: {
    fontSize: 30,
    flexShrink: 0,
  },

  documentIconPdf: {
    color: "#d3463b",
  },

  documentIconFile: {
    color: "#5f6368",
  },

  documentTextCol: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
  },

  documentName: {
    fontSize: 13,
    lineHeight: 1.3,
    color: "#111",
    wordBreak: "break-word",
  },

  documentMeta: {
    fontSize: 11,
    lineHeight: 1.4,
    color: "#667781",
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },

  documentDownloadIcon: {
    color: "#667781",
    fontSize: 22,
    flexShrink: 0,
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const MessagesList = ({ ticketId, isGroup }) => {
  const classes = useStyles();

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);
  const [mentionMap, setMentionMap] = useState({});

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);

    currentTicketId.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber },
          });

          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
          }

          if (pageNumber === 1 && data.messages.length > 1) {
            scrollToBottom();
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId]);

  // Mapa numero->nome dos participantes do grupo, para renderizar mencoes
  // (@numero) como @Nome, igual ao WhatsApp oficial. Chave = ultimos 8 digitos.
  useEffect(() => {
    if (!isGroup || !ticketId) {
      setMentionMap({});
      return;
    }
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(
          "/tickets/" + ticketId + "/group-participants"
        );
        if (!active) return;
        const map = {};
        (data.participants || []).forEach((pt) => {
          const digits = String(pt.number || "").replace(/\D/g, "");
          if (!digits || !pt.name) return;
          map[digits] = pt.name;
          const last8 = digits.slice(-8);
          if (last8 && !map[last8]) map[last8] = pt.name;
        });
        setMentionMap(map);
      } catch (err) {
        // grupo sem participantes resolviveis: mantem numero cru
      }
    })();
    return () => {
      active = false;
    };
  }, [ticketId, isGroup]);
  const resolveMentions = (text) => {
    if (!text || typeof text !== "string") return text;
    if (!text.includes("@")) return text;
    return text.replace(/@(\d{5,})/g, (full, num) => {
      const name = mentionMap[num] || mentionMap[String(num).slice(-8)];
      return name ? "*@" + name + "*" : full;
    });
  };


  useEffect(() => {
    const socket = openSocket();

    socket.on("connect", () => socket.emit("joinChatBox", ticketId));

    socket.on("appMessage", (data) => {
      if (data.action === "create") {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId]);

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = () => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({});
    }
  };

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const checkMessageMedia = (message) => {
    if (message.mediaType === "location" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|')
      let imageLocation = locationParts[0]
      let linkLocation = locationParts[1]

      let descriptionLocation = null

      if (locationParts.length > 2)
        descriptionLocation = message.body.split('|')[2]

      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    }
    else if (message.mediaType === "vcard") {
      //console.log("vcard")
      //console.log(message)
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0]?.number} />
    }
    /*else if (message.mediaType === "multi_vcard") {
      console.log("multi_vcard")
      console.log(message)
    	
      if(message.body !== null && message.body !== "") {
        let newBody = JSON.parse(message.body)
        return (
          <>
            {
            newBody.map(v => (
              <VcardPreview contact={v.name} numbers={v.number} />
            ))
            }
          </>
        )
      } else return (<></>)
    }*/
    else if (/\.webp$/i.test(message.mediaUrl) && message.mediaType === "image") {
      // Figurinha (sticker): renderiza como imagem pequena/transparente, estilo
      // WhatsApp (antes o .webp caia como arquivo para download).
      return (
        <img
          src={message.mediaUrl}
          alt="figurinha"
          style={{ width: 150, height: 150, objectFit: "contain" }}
        />
      );
    }
    else if ( /^.*\.(jpe?g|png|gif|webp)?$/i.exec(message.mediaUrl) && message.mediaType === "image") {
      return <ModalImageCors imageUrl={message.mediaUrl} />;
    } else if (message.mediaType === "audio") {
      return <Audio url={message.mediaUrl} message={message} />
    } else if (message.mediaType === "video") {
      return (
        <video
          className={classes.messageMedia}
          src={message.mediaUrl}
          controls
        />
      );
    } else {
      const original = getOriginalFilename(message.mediaUrl);
      const isPdf =
        /\.pdf$/i.test(message.mediaUrl) || message.mediaType === "application";
      return (
        <div className={classes.documentCard}>
          {isPdf && (
            <div className={classes.documentPreview}>
              <embed
                src={`${message.mediaUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                type="application/pdf"
                title={original}
              />
            </div>
          )}
          <a
            className={classes.documentRow}
            href={message.mediaUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {isPdf ? (
              <PictureAsPdf
                className={clsx(classes.documentIcon, classes.documentIconPdf)}
              />
            ) : (
              <InsertDriveFile
                className={clsx(classes.documentIcon, classes.documentIconFile)}
              />
            )}
            <span className={classes.documentTextCol}>
              <span className={classes.documentName}>{original}</span>
              <span className={classes.documentMeta}>
                {(original.split(".").pop() || "arquivo")}
              </span>
            </span>
            <GetApp className={classes.documentDownloadIcon} />
          </a>
        </div>
      );
    }
  };

  const renderMessageAck = (message) => {
    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 1) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3 || message.ack === 4) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
    }
  };

  const renderDailyTimestamps = (message, index) => {
    const elements = [];
    const isNewDay =
      index === 0 ||
      !isSameDay(
        parseISO(messagesList[index].createdAt),
        parseISO(messagesList[index - 1].createdAt)
      );

    if (isNewDay) {
      elements.push(
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
          </div>
        </span>
      );
    }
    if (index === messagesList.length - 1) {
      elements.push(
        <div
          key={`ref-${message.createdAt}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      );
    }
    return elements;
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderReactions = (message, fromMe) => {
    let reactions = [];
    try {
      const parsed = JSON.parse(message.reactions || "[]");
      if (Array.isArray(parsed)) reactions = parsed;
    } catch (err) {}
    if (!reactions.length) return null;
    const counts = {};
    reactions.forEach(r => {
      counts[r.emoji] = (counts[r.emoji] || 0) + 1;
    });
    return (
      <span
        style={{
          position: "absolute",
          bottom: -11,
          [fromMe ? "right" : "left"]: 8,
          display: "inline-flex",
          gap: 2,
          background: "#fff",
          border: "1px solid #e2e8f0",
          borderRadius: 12,
          padding: "0 6px",
          fontSize: 13,
          lineHeight: "20px",
          boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
          zIndex: 2
        }}
      >
        {Object.entries(counts).map(([emoji, count]) => (
          <span key={emoji}>
            {emoji}
            {count > 1 ? count : ""}
          </span>
        ))}
      </span>
    );
  };

  const scrollToQuoted = quotedId => {
    const el = document.getElementById(`wa-msg-${quotedId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    const prev = el.style.backgroundColor;
    el.style.transition = "background-color .3s";
    el.style.backgroundColor = "rgba(255,212,59,0.5)";
    setTimeout(() => { el.style.backgroundColor = prev; }, 1400);
  };

  const quotedPreviewText = m => {
    if (!m) return "";
    const t = String(m.mediaType || "");
    if (t === "image") return "📷 Foto";
    if (t === "audio") return "🎤 Áudio";
    if (t === "video") return "🎬 Vídeo";
    if (t === "vcard") return "👤 Contato";
    if (t === "location") return "📍 Localização";
    if (m.mediaUrl) return "📎 " + (String(m.mediaUrl).split("/").pop() || "Arquivo");
    return m.body || "Mídia";
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        onClick={() => scrollToQuoted(message.quotedMsg?.id)}
        style={{ cursor: "pointer" }}
        title="Ir para a mensagem original"
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          <span
            className={
              message.quotedMsg?.fromMe
                ? classes.quotedAuthorMe
                : classes.quotedAuthorOther
            }
          >
            {message.quotedMsg?.fromMe
              ? "Você"
              : message.quotedMsg?.contact?.name || "Contato"}
          </span>
          {quotedPreviewText(message.quotedMsg)}
        </div>
      </div>
    );
  };

  const renderMessages = () => {
    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div id={`wa-msg-${message.id}`} className={classes.messageLeft}>
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {isGroup && (
                  <span className={classes.messageContactName}>
                    {message.contact?.name}
                  </span>
                )}
                {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
                  //|| message.mediaType === "multi_vcard" 
                ) && checkMessageMedia(message)}
                <div className={classes.textContentItem}>
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {getDisplayBody(message) ? (
                    <MarkdownWrapper>{resolveMentions(getDisplayBody(message))}</MarkdownWrapper>
                  ) : null}
                  <span className={classes.timestamp}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                  </span>
                  {renderReactions(message, false)}
                </div>
              </div>
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderMessageDivider(message, index)}
              <div id={`wa-msg-${message.id}`} className={classes.messageRight}>
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {(message.mediaUrl || message.mediaType === "location" || message.mediaType === "vcard"
                  //|| message.mediaType === "multi_vcard" 
                ) && checkMessageMedia(message)}
                <div
                  className={clsx(classes.textContentItem, {
                    [classes.textContentItemDeleted]: message.isDeleted,
                  })}
                >
                  {message.isDeleted && (
                    <Block
                      color="disabled"
                      fontSize="small"
                      className={classes.deletedIcon}
                    />
                  )}
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {getDisplayBody(message) ? (
                    <MarkdownWrapper>{resolveMentions(getDisplayBody(message))}</MarkdownWrapper>
                  ) : null}
                  <span className={classes.timestamp}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                    {renderMessageAck(message)}
                  </span>
                  {renderReactions(message, true)}
                </div>
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>Say hello to your new contact!</div>;
    }
  };

  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />
      <div
        id="messagesList"
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {messagesList.length > 0 ? renderMessages() : []}
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;